import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ResponderCompany } from "@/services";
import { toast } from "sonner";
import { queries } from "../services/queries";
import { components } from "@/lib/api/openAPITypesV1";
type IResponderCompany = components["schemas"]["ResponderCompany"];

export const useAllResponderCompanies = () => {
  return useQuery({
    ...queries.responderCompanies.all,
    queryFn: () => {
      console.log("useAllResponderCompanies");
      return ResponderCompany.getAllResponderCompanies();
    },
    refetchOnWindowFocus: false,
  });
};

export const useResponderAdmin = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["responderCompanies", { userId }],
    queryFn: () => ResponderCompany.getResponderAdmin(userId),
    refetchOnWindowFocus: false,
  });
};

export const useResponderCompanyById = (companyId: number | undefined) => {
  return useQuery<IResponderCompany | null>({
    queryKey: ["responderCompanies", "detail", { companyId }],
    queryFn: () => ResponderCompany.getResponderCompanyById(companyId!),
    enabled: !!companyId && companyId > 0,
    refetchOnWindowFocus: false,
  });
};

export const useOverViewResponderCompanies = (
  pageSize: number,
  name: string | null = null,
  enabled: boolean = true
) => {
  return useInfiniteQuery({
    queryKey: ["responderCompanies", { pageSize, name }],
    queryFn: ({ pageParam = 1 }) =>
      ResponderCompany.getOverViewCompanies(pageParam, pageSize, name),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !allPages || !Array.isArray(allPages) || allPages.length === 0) {
        return undefined;
      }
      if (typeof lastPage.hasNextPage !== 'boolean' || typeof lastPage.pageNumber !== 'number') {
        return undefined;
      }
      return lastPage.hasNextPage ? lastPage.pageNumber + 1 : undefined;
    },
    initialPageParam: 1,
    enabled,
  });
};

export const useCreateResponderCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ResponderCompany.createCompany,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: queries.responderCompanies._def });
      return data;
    },
    onError: (err: Error) => {
      if (err.message) {
        toast.error(err.message);
      } else {
        toast.error("An error occurred");
      }
    },
    meta: {
      useError: true,
    },
  });
};

export const useUpdateResponderCompany = () => {
  const queryClient = useQueryClient();
  return useMutation<
    APIResult<IResponderCompany>,
    Error,
    IResponderCompanyUpdateRequest
  >({
    mutationFn: ResponderCompany.updateCompany,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: queries.responderCompanies._def });
      return data;
    },
    onError: (err: Error) => {
      if (err.message) {
        toast.error(err.message);
      } else {
        toast.error("An error occurred");
      }
    },
    meta: {
      useError: true,
    },
  });
};

export const useProcessCompanyRequest = () => {
  const queryClient = useQueryClient();
  return useMutation<
    CompanyRequestResponse,
    Error,
    IApproveRejectCompanyRequest
  >({
    mutationFn: ResponderCompany.processCompanyRequest,
    onSuccess: (data, variables) => {
      // Invalidate pending associations query to refresh the list
      queryClient.invalidateQueries({ queryKey: ["responderCompanies"] });

      // Show success message based on status (1 = Approved, 2 = Rejected)
      if (variables.Status === 1) {
        toast.success("User approved and added to company successfully");
      } else if (variables.Status === 2) {
        toast.success("User request rejected successfully");
      }

      return data;
    },
    onError: (err: Error) => {
      if (err.message) {
        toast.error(err.message);
      } else {
        toast.error("An error occurred while processing the request");
      }
    },
    meta: {
      useError: true,
    },
  });
};
